import { DOCUMENT, inject } from '@angular/core';
import { NavigationError, withNavigationErrorHandler } from '@angular/router';

const RELOAD_TIMESTAMP_KEY = 'welma:chunk-load-reload-at';
const RELOAD_COOLDOWN_MS = 10_000;

let reloading = false;

// After a deployment, lazy chunks referenced by a tab still running the previous build no longer exist.
// Each browser words the failure differently (Chrome/Edge, Firefox, Safari, and the MIME error raised
// when the server answers the missing chunk with index.html).
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /is not a valid JavaScript MIME type/i,
];

/**
 * Whether `withChunkLoadErrorRecovery` is reloading the page. The failed navigation still reaches the
 * `ErrorHandler`, which should then stay silent instead of inviting the user to refresh.
 */
export function isReloadingAfterChunkLoadError(): boolean {
  return reloading;
}

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Router feature reloading the page on the navigation target when a lazy chunk fails to load,
 * so a tab opened before a deployment picks up the new build instead of staying broken.
 * A cooldown prevents a reload loop when the chunk is genuinely unavailable.
 */
export function withChunkLoadErrorRecovery() {
  return withNavigationErrorHandler((navigationError: NavigationError) => {
    if (!isChunkLoadError(navigationError.error)) {
      return;
    }
    const window = inject(DOCUMENT).defaultView;
    if (!window || reloadedRecently(window) || !markReload(window)) {
      return;
    }
    reloading = true;
    window.location.assign(navigationError.url);
  });
}

function reloadedRecently(window: Window): boolean {
  try {
    const reloadedAt = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY));
    return Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;
  } catch {
    return true;
  }
}

// Without sessionStorage there is no loop protection, so the reload is skipped.
function markReload(window: Window): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
